import React, { useState, useEffect } from 'react';
import apiService from '../../services/api';
import type { SonarQubeMetrics, SonarQubeIssue, SonarQubeHotspot, SonarQubeProject } from '../../types/api';

const SonarQubeComponent: React.FC = () => {
  const [projects, setProjects] = useState<SonarQubeProject[]>([]);
  const [configMissing, setConfigMissing] = useState<string[] | null>(null);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [metrics, setMetrics] = useState<SonarQubeMetrics | null>(null);
  const [issues, setIssues] = useState<SonarQubeIssue[]>([]);
  const [hotspots, setHotspots] = useState<SonarQubeHotspot[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIssueType, setSelectedIssueType] = useState<string>('all');
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchProjectData(selectedProject);
    }
  }, [selectedProject]);

  const fetchProjects = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Ask what is configured before asking for data. Without this, an
      // unconfigured integration fails on the first data call and reports that
      // call's error, which sends people looking at the wrong thing.
      const health = await apiService.getSonarQubeHealth();
      const missing: string[] = health?.data?.missing ?? [];

      if (missing.length) {
        setConfigMissing(missing);
        setProjects([]);
        return;
      }
      setConfigMissing(null);

      const response = await apiService.getSonarQubeProjects();
      if (response.success && response.data) {
        setProjects(response.data.components || []);
        if (response.data.components && response.data.components.length > 0) {
          setSelectedProject(response.data.components[0].key);
        }
      } else {
        setError('Failed to fetch SonarQube projects');
      }
    } catch (error) {
      console.error('Error fetching SonarQube projects:', error);
      // The backend says why ("SONARQUBE_ORGANIZATION is not set",
      // "Insufficient privileges"), and each needs a different fix.
      const reason = error instanceof Error ? error.message : 'unknown error';
      setError(`Failed to fetch SonarQube projects: ${reason}`);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchProjectData = async (projectKey: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const [metricsResponse, issuesResponse, hotspotsResponse] = await Promise.all([
        apiService.getSonarQubeMetrics(projectKey),
        apiService.getSonarQubeIssues(projectKey),
        apiService.getSonarQubeHotspots(projectKey)
      ]);

      if (metricsResponse.success && metricsResponse.data) {
        setMetrics(metricsResponse.data);
      }
      if (issuesResponse.success && issuesResponse.data) {
        setIssues(issuesResponse.data.issues || []);
      }
      if (hotspotsResponse.success && hotspotsResponse.data) {
        setHotspots(hotspotsResponse.data.hotspots || []);
      }
      if (!metricsResponse.success && !issuesResponse.success && !hotspotsResponse.success) {
        setError('Failed to fetch project data');
      }
    } catch (error) {
      console.error('Error fetching project data:', error);
      setError('Failed to fetch project data. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const triggerAnalysis = async () => {
    if (!selectedProject) return;
    setIsAnalyzing(true);
    setError(null);
    try {
      const response = await apiService.triggerSonarQubeAnalysis(selectedProject);
      if (response.success) {
        setTimeout(() => { fetchProjectData(selectedProject); }, 2000);
      } else {
        setError(response.message || 'Failed to trigger analysis');
      }
    } catch (error) {
      console.error('Error triggering analysis:', error);
      setError('Failed to trigger analysis. Please try again.');
    } finally {
      setIsAnalyzing(false);
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'BLOCKER':
      case 'CRITICAL':
      case 'HIGH':
        return 'bg-red-100 text-red-700';
      case 'MAJOR':
      case 'MEDIUM':
        return 'bg-orange-100 text-orange-700';
      case 'MINOR':
      case 'LOW':
        return 'bg-yellow-100 text-yellow-700';
      case 'INFO':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'BUG': return 'text-red-600';
      case 'VULNERABILITY': return 'text-orange-600';
      case 'CODE_SMELL': return 'text-yellow-600';
      default: return 'text-gray-600';
    }
  };

  const getRatingColor = (rating: number) => {
    if (rating >= 4) return 'text-scarf-green';
    if (rating >= 3) return 'text-brand-blue-light';
    if (rating >= 2) return 'text-brand-red';
    return 'text-red-600';
  };

  const getQualityGateColor = (status: string) => {
    switch (status) {
      case 'OK': return 'text-scarf-green';
      case 'WARN': return 'text-orange-600';
      case 'ERROR': return 'text-red-600';
      default: return 'text-gray-600';
    }
  };

  const filteredIssues = selectedIssueType === 'all'
    ? issues
    : issues.filter(issue => issue.type === selectedIssueType);

  const issueTypes = ['all', ...Array.from(new Set(issues.map(issue => issue.type)))];

  if (isLoading && !projects.length) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-gray-600 text-lg">Loading static analysis data…</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {configMissing && (
        <div className="bg-caution-bg border border-caution/30 rounded-xl p-4">
          <h3 className="text-ink font-bold mb-1">Static analysis is not connected</h3>
          <p className="text-ink-2 text-sm">
            Nothing is configured for this environment, so there is no server to read from.
            Missing: <code className="text-ink">{configMissing.join(', ')}</code>.
          </p>
          <p className="text-ink-2 text-sm mt-2">
            SonarQube runs from <code className="text-ink">docker-compose.sonarqube.yml</code> for
            local development. A deployed environment needs its own server, or a hosted one, and
            both variables set before this screen has anything to show.
          </p>
        </div>
      )}

      {error && !configMissing && (
        <div className="bg-red-50 border border-red-200 text-brand-red rounded-xl p-4">
          {error}
        </div>
      )}

      {/* Project Selection */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-title font-bold text-deep-navy">Project Selection</h3>
          <button
            onClick={fetchProjects}
            disabled={isLoading}
            className="bg-brand-blue-light hover:bg-brand-blue disabled:bg-brand-blue-light/50 text-white font-bold px-4 py-2 rounded-xl transition-colors"
          >
            Refresh
          </button>
        </div>

        <div className="flex flex-wrap gap-4">
          {projects.map((project) => (
            <button
              key={project.key}
              onClick={() => setSelectedProject(project.key)}
              className={`px-6 py-3 rounded-xl font-bold transition-colors ${
                selectedProject === project.key
                  ? 'bg-brand-blue text-white'
                  : 'bg-frost-white text-gray-600 hover:text-deep-navy hover:bg-ice-teal-light'
              }`}
            >
              {project.name}
              {project.qualityGate && (
                <span className={`ml-2 ${getQualityGateColor(project.qualityGate.status)}`}>
                  {project.qualityGate.status}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Metrics Overview */}
      {metrics && (
        <div className="grid md:grid-cols-4 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Bugs</h3>
            <div className="text-3xl font-bold text-red-600">{metrics.bugs}</div>
            <p className="text-gray-500 text-sm">Code defects</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Vulnerabilities</h3>
            <div className="text-3xl font-bold text-orange-600">{metrics.vulnerabilities}</div>
            <p className="text-gray-500 text-sm">Security issues</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Code Smells</h3>
            <div className="text-3xl font-bold text-yellow-600">{metrics.codeSmells}</div>
            <p className="text-gray-500 text-sm">Maintainability issues</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Coverage</h3>
            <div className="text-3xl font-bold text-brand-blue-light">{metrics.coverage.toFixed(1)}%</div>
            <p className="text-gray-500 text-sm">Test coverage</p>
          </div>
        </div>
      )}

      {/* Quality Ratings */}
      {metrics && (
        <div className="grid md:grid-cols-3 gap-6">
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Maintainability</h3>
            <div className={`text-3xl font-bold ${getRatingColor(metrics.maintainabilityRating)}`}>
              {metrics.maintainabilityRating}/5
            </div>
            <p className="text-gray-500 text-sm">Code maintainability</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Reliability</h3>
            <div className={`text-3xl font-bold ${getRatingColor(metrics.reliabilityRating)}`}>
              {metrics.reliabilityRating}/5
            </div>
            <p className="text-gray-500 text-sm">Code reliability</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
            <h3 className="text-lg font-title font-bold text-deep-navy mb-2">Security</h3>
            <div className={`text-3xl font-bold ${getRatingColor(metrics.securityRating)}`}>
              {metrics.securityRating}/5
            </div>
            <p className="text-gray-500 text-sm">Security rating</p>
          </div>
        </div>
      )}

      {/* Quick Actions */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Static Code Testing Actions</h3>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={triggerAnalysis}
            disabled={isAnalyzing || !selectedProject}
            className="bg-brand-red hover:bg-brand-red/90 disabled:bg-brand-red/50 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            {isAnalyzing ? 'Analyzing...' : 'Run Analysis'}
          </button>
          <button
            onClick={() => selectedProject && fetchProjectData(selectedProject)}
            disabled={isLoading}
            className="bg-brand-blue-light hover:bg-brand-blue disabled:bg-brand-blue-light/50 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            {isLoading ? 'Refreshing...' : 'Refresh Data'}
          </button>
          <button
            onClick={() => window.open(`${process.env.REACT_APP_SONARQUBE_URL || 'http://localhost:9000'}/dashboard?id=${selectedProject}`, '_blank')}
            disabled={!selectedProject}
            className="bg-scarf-green hover:bg-scarf-green/90 disabled:bg-scarf-green/50 text-white font-bold px-6 py-3 rounded-xl transition-colors"
          >
            Open in SonarQube
          </button>
        </div>
      </div>

      {/* Issues Filter */}
      <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-title font-bold text-deep-navy">Code Issues</h3>
          <select
            value={selectedIssueType}
            onChange={(e) => setSelectedIssueType(e.target.value)}
            className="bg-frost-white border border-ice-teal-light rounded-lg px-4 py-2 text-gray-800 focus:outline-none focus:border-brand-blue"
          >
            {issueTypes.map((type) => (
              <option key={type} value={type}>
                {type === 'all' ? 'All Types' : type}
              </option>
            ))}
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-gray-800">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Type</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Severity</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Message</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">File</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Line</th>
                <th className="text-left py-3 px-4 text-deep-navy font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredIssues.map((issue) => (
                <tr key={issue.id} className="border-b border-gray-100">
                  <td className="py-3 px-4">
                    <span className={`font-bold ${getTypeColor(issue.type)}`}>
                      {issue.type}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-bold ${getSeverityColor(issue.severity)}`}>
                      {issue.severity}
                    </span>
                  </td>
                  <td className="py-3 px-4">{issue.message}</td>
                  <td className="py-3 px-4 font-mono text-sm">{issue.file}</td>
                  <td className="py-3 px-4">{issue.line}</td>
                  <td className="py-3 px-4">
                    <span className="px-2 py-1 rounded text-xs font-bold bg-gray-100 text-gray-600">
                      {issue.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Security Hotspots */}
      {hotspots.length > 0 && (
        <div className="bg-white rounded-2xl p-6 border border-ice-teal-light shadow-sm">
          <h3 className="text-xl font-title font-bold text-deep-navy mb-4">Security Hotspots</h3>
          <div className="space-y-4">
            {hotspots.map((hotspot) => (
              <div key={hotspot.id} className="bg-frost-white rounded-xl p-4 border border-ice-teal-light">
                <div className="flex justify-between items-start mb-2">
                  <h4 className="font-bold text-deep-navy">{hotspot.title}</h4>
                  <span className={`px-2 py-1 rounded text-xs font-bold ${getSeverityColor(hotspot.severity)}`}>
                    {hotspot.severity}
                  </span>
                </div>
                <p className="text-gray-600 text-sm mb-2">{hotspot.description}</p>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">File:</span>
                    <span className="text-gray-800 ml-2 font-mono">{hotspot.file}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Line:</span>
                    <span className="text-gray-800 ml-2">{hotspot.line}</span>
                  </div>
                </div>
                <div className="mt-2 text-xs text-gray-400">
                  Status: {hotspot.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default SonarQubeComponent;
